import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { withLocalize } from 'react-localize-redux';
import { useSelector } from 'react-redux';
import AutoCompleteField, { AUTOCOMPLETE_LAYOUTS } from './AutoCompleteField';
import { getIsAgency } from '../../selectors/user';
import { getOrgAgencies } from '../../selectors/agencies';
import { filterAgenciesByAppPermissions, filterAgenciesByPartnerPermissions } from '../../utils/agencyPermissions';
import css from './AgencySetupDropdown.css';

const MY_ORG_VALUE = null;

const AgencySetupDropdown = ({
    value = MY_ORG_VALUE,
    onChange = () => {},
    disabled = false,
    translate = () => {},
    containerClass = '',
    selectedAppSites = null,
    selectedPartnerName = null,
    configuredPartners = null,
    label = 'STATIC.PAGES.AGENCY_SETUP_DROPDOWN.LABEL',
    labelTooltip = 'STATIC.PAGES.AGENCY_SETUP_DROPDOWN.TOOLTIP',
    hideTooltip = false,
    labelClassName = '',
    layout = AUTOCOMPLETE_LAYOUTS.VERTICAL,
    containerClassName = '',
}) => {
    const isAgency = useSelector(getIsAgency);
    const agencies = useSelector(getOrgAgencies);
    const isLoaded = !!agencies;

    const options = useMemo(() => {
        if (disabled && !agencies.find(agency => agency.agency_id === value)) {
            return [
                {
                    value,
                    label: value,
                },
            ];
        }

        const myOrgOption = {
            value: MY_ORG_VALUE,
            label: translate('STATIC.PAGES.AGENCY_SETUP_DROPDOWN.MY_ORG'),
        };

        if (agencies.length === 0) {
            return [myOrgOption];
        }

        let filteredAgencies = agencies.map(agency => ({
            agency,
            hasAppPermission: true,
            hasPartnerPermission: true,
        }));

        // Apply app site filtering if selectedAppSites is provided
        if (selectedAppSites?.length) {
            const appFiltered = filterAgenciesByAppPermissions(agencies, selectedAppSites);
            filteredAgencies = filteredAgencies.map(({ agency }) => {
                const appResult = appFiltered.find(({ agency: a }) => a.agency_id === agency.agency_id);
                return {
                    agency,
                    hasAppPermission: appResult?.hasPermission ?? true,
                    hasPartnerPermission: true,
                };
            });
        }

        // Apply partner filtering if selectedPartnerName is provided
        if (selectedPartnerName) {
            const partnerFiltered = filterAgenciesByPartnerPermissions(
                filteredAgencies.map(({ agency }) => agency),
                selectedPartnerName
            );
            filteredAgencies = filteredAgencies.map(({ agency, hasAppPermission }) => {
                const partnerResult = partnerFiltered.find(({ agency: a }) => a.agency_id === agency.agency_id);
                return {
                    agency,
                    hasAppPermission,
                    hasPartnerPermission: partnerResult?.hasPermission ?? true,
                };
            });
        }

        const agencyOptions = filteredAgencies.map(({ agency, hasAppPermission, hasPartnerPermission }) => {
            const hasPermission = hasAppPermission && hasPartnerPermission;
            return {
                value: agency.agency_id,
                label: agency.display_name,
                ...(agency.company_logo && {
                    icon: <img className={css.agencyOptionIcon} src={agency.company_logo} alt="" />,
                }),
                isDisabled: !hasPermission,
                extra: !hasPermission
                    ? translate(
                          !hasAppPermission && !hasPartnerPermission
                              ? 'STATIC.PAGES.AGENCY_SETUP_DROPDOWN.SOURCE_NOT_PERMITTED' // Both failed, show source (more specific)
                              : !hasAppPermission
                              ? 'STATIC.PAGES.AGENCY_SETUP_DROPDOWN.APP_NOT_PERMITTED'
                              : 'STATIC.PAGES.AGENCY_SETUP_DROPDOWN.SOURCE_NOT_PERMITTED'
                      )
                    : null,
            };
        });

        return [myOrgOption, ...agencyOptions];
    }, [agencies, translate, selectedAppSites, selectedPartnerName, configuredPartners, disabled, value]);

    const selectedOption = useMemo(() => {
        return options.find(opt => opt.value === value) || options[0];
    }, [options, value]);

    // Don't show for agencies, if not loaded, or if no related agencies
    if (isAgency || !isLoaded || agencies.length === 0) {
        return null;
    }

    return (
        <div className={`${css.container} ${containerClassName}`}>
            <AutoCompleteField
                label={label}
                labelTooltip={hideTooltip ? null : labelTooltip}
                labelClassName={labelClassName}
                layout={layout}
                options={options}
                value={selectedOption}
                isMulti={false}
                disabled={disabled}
                onChange={option => {
                    const newValue = option?.value ?? MY_ORG_VALUE;
                    onChange(newValue);
                }}
                containerClass={containerClass}
                placeholder="STATIC.PAGES.AGENCY_SETUP_DROPDOWN.PLACEHOLDER"
            />
        </div>
    );
};

AgencySetupDropdown.propTypes = {
    value: PropTypes.string,
    onChange: PropTypes.func,
    disabled: PropTypes.bool,
    translate: PropTypes.func,
    containerClass: PropTypes.string,
    selectedAppSites: PropTypes.arrayOf(
        PropTypes.shape({
            site_public_id: PropTypes.string,
            publicId: PropTypes.string,
        })
    ),
    selectedPartnerName: PropTypes.string,
    configuredPartners: PropTypes.arrayOf(
        PropTypes.shape({
            name: PropTypes.string,
        })
    ),
    label: PropTypes.string,
    labelTooltip: PropTypes.string,
    hideTooltip: PropTypes.bool,
    labelClassName: PropTypes.string,
    layout: PropTypes.oneOf(Object.values(AUTOCOMPLETE_LAYOUTS)),
    containerClassName: PropTypes.string,
};

export default withLocalize(AgencySetupDropdown);
